import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Mail, RotateCcw, Trash2, Copy, MessageCircle, Receipt, Stamp } from "lucide-react";
import { api, API } from "../../lib/api";
import { DeliverEbookDialog, waLink } from "./DeliverEbookDialog";
import { LaunchOfferSettings } from "./LaunchOfferSettings";
import { SalesDashboard } from "./SalesDashboard";
import { ReviewsList } from "./ReviewsList";
import { PendingDeliveries } from "./PendingDeliveries";

const opts = { withCredentials: true };

export function EbookSalesPanel() {
  const [sales, setSales] = useState([]);
  const [extracts, setExtracts] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/ebook-sales", opts).then((r) => setSales(r.data)).catch(() => setSales([]));
  useEffect(() => { load(); api.get("/admin/extracts", opts).then((r) => setExtracts(r.data)).catch(() => {}); }, []);

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch { toast.error("Action impossible"); } };
  const resend = (s) => act(async () => { const r = await api.post(`/admin/ebook-sales/${s.id}/resend`, {}, opts); if (r.data.email_status !== "envoye") throw new Error(); }, "E-mail renvoyé");
  const regen = (s) => act(() => api.post(`/admin/ebook-sales/${s.id}/regenerate`, {}, opts), "Exemplaire marqué recréé (même lien)");
  const reset = (s) => act(() => api.post(`/admin/ebook-sales/${s.id}/reset`, {}, opts), "Téléchargements remis à zéro");
  const remove = (s) => window.confirm(`Supprimer la vente ${s.ref} ? Le lien de l'acheteur ne fonctionnera plus.`) && act(() => api.delete(`/admin/ebook-sales/${s.id}`, opts), "Vente supprimée");

  return (
    <div data-testid="ebook-sales-panel">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h2 className="font-serif text-4xl text-[#1A1A1A] font-light">Ventes numériques</h2>
        <button onClick={() => setOpen(true)} data-testid="new-ebook-sale-button" className="bg-[#2C402E] text-[#FAF9F6] px-4 py-2 text-sm font-body flex items-center gap-2"><Plus size={14} /> Nouvelle vente</button>
      </div>
      <p className="text-sm font-body text-[#757575] mb-6 max-w-3xl">Après réception du paiement, validez la vente : une copie marquée au nom de l'acheteur est créée et un lien personnel (lecture en ligne + 3 téléchargements) lui est envoyé. Depuis « Commandes », le bouton « Livrer » pré-remplit ce formulaire.</p>
      <PendingDeliveries onDone={load} />
      <SalesDashboard refreshKey={sales.length} />
      <LaunchOfferSettings />
      <div className="overflow-x-auto border border-[#D1CFC7]">
        <table className="w-full text-xs font-body whitespace-nowrap">
          <thead className="bg-[#F4F1EA] text-left uppercase tracking-wider text-[#4A4A4A]">
            <tr>{["Date", "Réf.", "Livre", "Acheteur", "Transaction", "Format", "Téléch.", "E-mail", ""].map((h) => <th key={h} className="p-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {sales.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-[#757575]" data-testid="ebook-sales-empty">Aucune vente numérique pour l'instant.</td></tr>}
            {sales.map((s) => (
              <tr key={s.id} data-testid={`ebook-sale-row-${s.ref}`} className="border-t border-[#E8E4D9]">
                <td className="p-3">{new Date(s.created_at).toLocaleDateString("fr-FR")}</td>
                <td className="p-3 font-mono">{s.ref}</td>
                <td className="p-3 max-w-[180px] truncate">{s.title}</td>
                <td className="p-3">{s.first_name} {s.last_name}<span className="block text-[#757575]">{s.email}</span></td>
                <td className="p-3 font-mono">{s.transaction_ref}</td>
                <td className="p-3">{s.format.toUpperCase()}</td>
                <td className="p-3" data-testid={`ebook-sale-downloads-${s.ref}`}>{s.downloads} / {s.max_downloads}</td>
                <td className="p-3">{s.email_status === "envoye" ? "Envoyé" : "Non envoyé"}</td>
                <td className="p-3 text-right space-x-3">
                  <button onClick={() => navigator.clipboard.writeText(s.link).then(() => toast.success("Lien copié"))} title="Copier le lien" data-testid={`ebook-sale-copy-${s.ref}`} className="text-[#2C402E]"><Copy size={13} /></button>
                  {s.phone && <a href={waLink(s)} target="_blank" rel="noreferrer" title="WhatsApp" data-testid={`ebook-sale-wa-${s.ref}`} className="text-[#2C402E] inline-block"><MessageCircle size={13} /></a>}
                  <a href={`${API}/admin/ebook-sales/${s.id}/receipt`} title="Reçu PDF" data-testid={`ebook-sale-receipt-${s.ref}`} className="text-[#2C402E] inline-block"><Receipt size={13} /></a>
                  <button onClick={() => resend(s)} title="Renvoyer l'e-mail" data-testid={`ebook-sale-resend-${s.ref}`} className="text-[#2C402E]"><Mail size={13} /></button>
                  <button onClick={() => regen(s)} title="Recréer l'exemplaire marqué (même lien)" data-testid={`ebook-sale-regen-${s.ref}`} className="text-[#2C402E]"><Stamp size={13} /></button>
                  <button onClick={() => reset(s)} title="Remettre les téléchargements à zéro" data-testid={`ebook-sale-reset-${s.ref}`} className="text-[#2C402E]"><RotateCcw size={13} /></button>
                  <button onClick={() => remove(s)} title="Supprimer" data-testid={`ebook-sale-delete-${s.ref}`} className="text-[#9E4735]"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ReviewsList />
      <DeliverEbookDialog order={null} extracts={extracts} open={open} onOpenChange={setOpen} onDone={load} />
    </div>
  );
}
